/**
 * Campaign Profile
 *
 * Structured data model for GM preferences captured during the "Taste Interview".
 * This profile feeds into AI directors (MobDirector, StoryDirector, Narrator)
 * and asset generators (Session Kit, One-Pager).
 */
import { z } from 'zod';
// =============================================================================
// FUN TYPES (Pick 2)
// =============================================================================
export const FunTypeSchema = z.enum([
    'tactical_combat',
    'exploration',
    'mystery',
    'intrigue',
    'horror',
    'character_drama',
    'comedy',
    'domain_play',
]);
export const FUN_TYPE_LABELS = {
    tactical_combat: { label: 'Tactical Combat', description: 'Crunchy battles, grid tactics, resource management' },
    exploration: { label: 'Exploration', description: 'Discover new lands, dungeons, and secrets' },
    mystery: { label: 'Mystery', description: 'Clues, investigations, piecing together the truth' },
    intrigue: { label: 'Intrigue', description: 'Politics, factions, social maneuvering' },
    horror: { label: 'Horror', description: 'Dread, survival, things that go bump' },
    character_drama: { label: 'Character Drama', description: 'Personal arcs, relationships, emotional stakes' },
    comedy: { label: 'Comedy', description: 'Laughs, absurdity, not taking things too seriously' },
    domain_play: { label: 'Domain Play', description: 'Build kingdoms, run guilds, manage organizations' },
};
// =============================================================================
// TONE SLIDERS (0-100)
// =============================================================================
export const ToneSlidersSchema = z.object({
    noblebright_grimdark: z.number().min(0).max(100).default(50),
    sincere_gonzo: z.number().min(0).max(100).default(30),
    heroic_lethal: z.number().min(0).max(100).default(30),
});
export const TONE_SLIDER_LABELS = {
    noblebright_grimdark: { left: 'Noblebright', right: 'Grimdark', description: 'How dark is the world?' },
    sincere_gonzo: { left: 'Sincere', right: 'Gonzo', description: 'Serious drama or wild absurdity?' },
    heroic_lethal: { left: 'Heroic', right: 'Lethal', description: 'Power fantasy or gritty survival?' },
};
// =============================================================================
// SETTING CONFIG
// =============================================================================
export const MagicLevelSchema = z.enum(['low', 'medium', 'wild']);
export const TechLevelSchema = z.enum(['bronze', 'medieval', 'renaissance', 'industrial', 'steampunk']);
export const SettingConfigSchema = z.object({
    mythic_grounded: z.number().min(0).max(100).default(50),
    magic_level: MagicLevelSchema.default('medium'),
    tech_level: TechLevelSchema.default('medieval'),
});
export const MAGIC_LEVEL_LABELS = {
    low: 'Low Magic - Rare and wondrous',
    medium: 'Medium Magic - Part of daily life',
    wild: 'Wild Magic - Pervasive and unpredictable',
};
export const TECH_LEVEL_LABELS = {
    bronze: 'Bronze Age - Chariots, city-states, myth',
    medieval: 'Medieval - Castles, feudalism, classic fantasy',
    renaissance: 'Renaissance - Gunpowder, printing, intrigue',
    industrial: 'Industrial - Steam, factories, urbanization',
    steampunk: 'Steampunk - Magitech, airships, gadgets',
};
// =============================================================================
// MORAL PHYSICS
// =============================================================================
export const MoralPhysicsSchema = z.enum([
    'clear_good_evil',
    'grey_choices',
    'everyone_compromised',
    'chaos_tempts',
]);
export const MORAL_PHYSICS_LABELS = {
    clear_good_evil: { label: 'Clear Good vs Evil', description: 'Heroes fight villains, righteousness prevails' },
    grey_choices: { label: 'Grey Choices', description: 'No easy answers, competing goods and lesser evils' },
    everyone_compromised: { label: 'Everyone\'s Compromised', description: 'Cynical world, survival requires dirty hands' },
    chaos_tempts: { label: 'Chaos Tempts Power', description: 'Corruption beckons, power comes at a price' },
};
// =============================================================================
// STRUCTURE TYPE
// =============================================================================
export const StructureTypeSchema = z.enum([
    'sandbox',
    'mission_of_week',
    'arc_driven',
    'hexcrawl',
    'mystery_web',
]);
export const STRUCTURE_TYPE_LABELS = {
    sandbox: { label: 'Sandbox', description: 'Open world, player-driven goals' },
    mission_of_week: { label: 'Mission of the Week', description: 'Episodic adventures, quest board style' },
    arc_driven: { label: 'Arc-Driven', description: 'Overarching story with character arcs' },
    hexcrawl: { label: 'Hexcrawl / West Marches', description: 'Exploration-focused, rotating cast' },
    mystery_web: { label: 'Mystery Web', description: 'Interconnected clues and conspiracies' },
};
// =============================================================================
// PACING CONFIG
// =============================================================================
export const PacingConfigSchema = z.object({
    combat_rp_ratio: z.number().min(0).max(100).default(50),
    player_agency: z.number().min(0).max(100).default(50),
});
export const PACING_SLIDER_LABELS = {
    combat_rp_ratio: { left: 'All Roleplay', right: 'All Combat', description: 'Balance of combat vs roleplay' },
    player_agency: { left: 'Surprise Me', right: 'Plot Beats', description: 'Emergent play vs scripted moments' },
};
// =============================================================================
// GROUP PATRON
// =============================================================================
export const GroupPatronSchema = z.enum([
    'crew',
    'cult',
    'clan',
    'merc_company',
    'ship',
    'rebellion',
    'guild',
    'custom',
]);
export const GROUP_PATRON_LABELS = {
    crew: { label: 'Crew', description: 'Thieves, heist team, scoundrels' },
    cult: { label: 'Cult / Religious Order', description: 'Bound by faith or secret doctrine' },
    clan: { label: 'Clan / Family', description: 'Blood ties, tribal loyalty' },
    merc_company: { label: 'Mercenary Company', description: 'Swords for hire, military unit' },
    ship: { label: 'Ship / Vehicle', description: 'Crew of a vessel (sea, air, space)' },
    rebellion: { label: 'Rebellion Cell', description: 'Fighting the power, underground' },
    guild: { label: 'Guild / Organization', description: 'Professional association, trade secrets' },
    custom: { label: 'Custom', description: 'Define your own group bond' },
};
// =============================================================================
// CONTENT BOUNDARIES
// =============================================================================
export const ContentBoundariesSchema = z.object({
    use_safety_tools: z.boolean().default(true),
    lines: z.array(z.string()).default([]),
    veils: z.array(z.string()).default([]),
});
// Common lines/veils for quick selection
export const COMMON_LINES = [
    'Harm to children',
    'Sexual violence',
    'Real-world hate groups',
    'Animal cruelty',
    'Torture (graphic)',
];
export const COMMON_VEILS = [
    'Romantic/sexual content',
    'Graphic gore',
    'Mental health crises',
    'Addiction',
    'Self-harm',
];
// =============================================================================
// PREP STYLE
// =============================================================================
export const PrepStyleSchema = z.enum(['improv_toolkits', 'prewritten_scenes']);
export const PREP_STYLE_LABELS = {
    improv_toolkits: { label: 'Improv & Toolkits', description: 'Generate prompts, tables, factions - I\'ll riff' },
    prewritten_scenes: { label: 'Prewritten Scenes', description: 'Give me scene scripts and set pieces' },
};
// =============================================================================
// FULL CAMPAIGN PROFILE
// =============================================================================
export const CampaignProfileSchema = z.object({
    version: z.literal(1),
    playset: z.string().optional(),
    funTypes: z.tuple([FunTypeSchema, FunTypeSchema]),
    tone: ToneSlidersSchema,
    setting: SettingConfigSchema,
    moralPhysics: MoralPhysicsSchema,
    structure: StructureTypeSchema,
    pacing: PacingConfigSchema,
    groupPatron: z.object({
        type: GroupPatronSchema,
        customName: z.string().optional(),
    }),
    boundaries: ContentBoundariesSchema,
    prepStyle: PrepStyleSchema,
    sessionOneImage: z.string().default(''),
    generatedAssets: z.object({
        onePagerId: z.string().optional(),
        sessionKitId: z.string().optional(),
        generatedAt: z.string().optional(),
    }).optional(),
});
// =============================================================================
// DEFAULT PROFILE
// =============================================================================
export const DEFAULT_CAMPAIGN_PROFILE = {
    version: 1,
    playset: 'standard_dnd',
    funTypes: ['tactical_combat', 'exploration'],
    tone: { noblebright_grimdark: 40, sincere_gonzo: 30, heroic_lethal: 30 },
    setting: { mythic_grounded: 50, magic_level: 'medium', tech_level: 'medieval' },
    moralPhysics: 'clear_good_evil',
    structure: 'mission_of_week',
    pacing: { combat_rp_ratio: 50, player_agency: 50 },
    groupPatron: { type: 'merc_company' },
    boundaries: { use_safety_tools: true, lines: [], veils: [] },
    prepStyle: 'improv_toolkits',
    sessionOneImage: '',
};
export const PLAYSETS = {
    mythic_bronze_age: {
        id: 'mythic_bronze_age',
        name: 'Mythic Bronze Age',
        tagline: 'Cults, omens & the weight of myth',
        description: 'Cults, taboos, seasonal omens, and the weight of myth. The gods are real and demand tribute.',
        coverImage: '/worlds/mythic_bronze_age.png',
        profile: {
            funTypes: ['character_drama', 'mystery'],
            tone: { noblebright_grimdark: 30, sincere_gonzo: 20, heroic_lethal: 40 },
            setting: { mythic_grounded: 10, magic_level: 'wild', tech_level: 'bronze' },
            moralPhysics: 'grey_choices',
            structure: 'arc_driven',
            groupPatron: { type: 'cult' },
            prepStyle: 'improv_toolkits',
        },
    },
    standard_dnd: {
        id: 'standard_dnd',
        name: 'Classic Adventure',
        tagline: 'Classic dungeon-crawl fantasy',
        description: 'Dungeons, dragons, treasure, and taverns. The tried-and-true fantasy adventure.',
        coverImage: '/worlds/standard_dnd.png',
        profile: {
            funTypes: ['tactical_combat', 'exploration'],
            tone: { noblebright_grimdark: 40, sincere_gonzo: 30, heroic_lethal: 30 },
            setting: { mythic_grounded: 50, magic_level: 'medium', tech_level: 'medieval' },
            moralPhysics: 'clear_good_evil',
            structure: 'mission_of_week',
            groupPatron: { type: 'merc_company' },
            prepStyle: 'improv_toolkits',
        },
    },
    chaos_champion: {
        id: 'chaos_champion',
        name: 'The Doomed Blade',
        tagline: 'Cursed blades & doomed antiheroes',
        description: 'Cursed blades, dark pacts, and tragic antiheroes. Power always comes at a price.',
        coverImage: '/worlds/chaos_champion.png',
        profile: {
            funTypes: ['character_drama', 'intrigue'],
            tone: { noblebright_grimdark: 80, sincere_gonzo: 20, heroic_lethal: 70 },
            setting: { mythic_grounded: 30, magic_level: 'wild', tech_level: 'medieval' },
            moralPhysics: 'chaos_tempts',
            structure: 'arc_driven',
            groupPatron: { type: 'cult' },
            prepStyle: 'improv_toolkits',
        },
    },
    steampunk_heist: {
        id: 'steampunk_heist',
        name: 'Steampunk Heist',
        tagline: 'Industrial-age crews & heists',
        description: 'Airships, gadgets, heists, and guilds. Everyone has an angle in the smoke-filled city.',
        coverImage: '/worlds/steampunk_heist.png',
        profile: {
            funTypes: ['intrigue', 'exploration'],
            tone: { noblebright_grimdark: 50, sincere_gonzo: 40, heroic_lethal: 40 },
            setting: { mythic_grounded: 70, magic_level: 'medium', tech_level: 'steampunk' },
            moralPhysics: 'everyone_compromised',
            structure: 'mission_of_week',
            groupPatron: { type: 'crew' },
            prepStyle: 'prewritten_scenes',
        },
    },
    tethered_city: {
        id: 'tethered_city',
        name: 'The Tethered City',
        tagline: 'Philosophical urban investigation',
        description: 'A city between planes, held together by belief. Six factions. A wound in reality. Your thoughts are not entirely your own.',
        coverImage: '/worlds/tethered_city.png',
        profile: {
            funTypes: ['intrigue', 'mystery'],
            tone: { noblebright_grimdark: 65, sincere_gonzo: 15, heroic_lethal: 45 },
            setting: { mythic_grounded: 25, magic_level: 'wild', tech_level: 'renaissance' },
            moralPhysics: 'grey_choices',
            structure: 'mystery_web',
            pacing: { combat_rp_ratio: 25, player_agency: 60 },
            groupPatron: { type: 'rebellion' },
            prepStyle: 'improv_toolkits',
        },
    },
};
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
/**
 * Merge a playset's profile with defaults to get a complete profile
 */
export function applyPlayset(playsetId) {
    const playset = PLAYSETS[playsetId];
    return {
        ...DEFAULT_CAMPAIGN_PROFILE,
        ...playset.profile,
        playset: playsetId,
    };
}
/**
 * Get a human-readable summary of the profile's tone
 */
export function getToneSummary(tone) {
    const parts = [];
    if (tone.noblebright_grimdark < 30)
        parts.push('hopeful');
    else if (tone.noblebright_grimdark > 70)
        parts.push('grimdark');
    if (tone.sincere_gonzo > 60)
        parts.push('gonzo');
    else if (tone.sincere_gonzo < 30)
        parts.push('sincere');
    if (tone.heroic_lethal > 60)
        parts.push('lethal');
    else if (tone.heroic_lethal < 30)
        parts.push('heroic');
    return parts.length > 0 ? parts.join(', ') : 'balanced';
}
//# sourceMappingURL=campaign-profile.js.map